import React, { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../../Context/GlobalContext';
import { toast } from "sonner";

const SalesAnalytics = ({
  adminId,
  productId,
  fromDate,
  toDate,
  autoFetch = true,
}) => {
  const { api } = useContext(GlobalContext);
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);

  const fetchSalesAnalytics = async () => {
    if (!adminId || !productId) {
      setLoading(false);
      return;
    }
    setAnalyticsData(null);
    setLoading(true);

    const minDelay = 500;
    const startTime = Date.now();
    try {
      const payload = {
        adminId: String(adminId),
        productId: String(productId),
        fromDate: fromDate || "",
        toDate: toDate || "",
      };
      const res = await api.post(`Admin/v1.0/getSalesAnalytics`, payload);
      if (res?.data?.responseCode === "00") {
        const elapsed = Date.now() - startTime;
        const remainingDelay = minDelay - elapsed;

        if (remainingDelay > 0) {
          setTimeout(
            () => setAnalyticsData(res.data.data || {}),
            remainingDelay
          );
        } else {
          setAnalyticsData(res.data.data || {});
        }
      } else {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch sales analytics"
        );
        setAnalyticsData({});
      }
    } catch (e) {
      toast.error("Something went wrong!");
      setAnalyticsData({});
    } finally {
      const elapsed = Date.now() - startTime;
      const remainingDelay = minDelay - elapsed;

      if (remainingDelay > 0) {
        setTimeout(() => setLoading(false), remainingDelay);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!autoFetch || !adminId || !productId) return;

    const handler = setTimeout(() => {
      fetchSalesAnalytics();
    }, 500); 

    return () => clearTimeout(handler);
  }, [adminId, productId, fromDate, toDate, autoFetch]);

  const ShimmerCard = () => (
    <div className="h-8 bg-gray-200 rounded animate-pulse w-full mt-1"></div>
  );

  return (
    <section className="p-4 bg-gray-50 shadow rounded-2xl mb-6 dark:bg-gray-800 transition-colors">
      <div className="max-w-8xl mx-auto space-y-8">
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="group bg-white p-5 rounded-xl shadow hover:shadow-lg transition border-l-4 border-green-500 hover:bg-green-500">
            {analyticsData && !loading ? (
              <>
                <p className="text-sm font-medium text-gray-500 group-hover:text-white">
                  Total Onboarding
                </p>
                <h2 className="text-2xl font-bold text-gray-800 group-hover:text-white">
                  {analyticsData.total_onboarding || 0}
                </h2>
              </>
            ) : (
              <ShimmerCard />
            )}
          </div>

          <div className="group bg-white p-5 rounded-xl shadow hover:shadow-lg transition border-l-4 border-blue-500 hover:bg-blue-500">
            {analyticsData && !loading ? (
              <>
                <p className="text-sm font-medium text-gray-500 group-hover:text-white">
                  Total Business
                </p>
                <h2 className="text-2xl font-bold text-gray-800 group-hover:text-white">
                  {analyticsData.total_business || 0}
                </h2>
              </>
            ) : (
              <ShimmerCard />
            )}
          </div>

          <div className="group bg-white p-5 rounded-xl shadow hover:shadow-lg transition border-l-4 border-purple-500 hover:bg-purple-500">
            {analyticsData && !loading ? (
              <>
                <p className="text-sm font-medium text-gray-500 group-hover:text-white">
                  Total KYC
                </p>
                <h2 className="text-2xl font-bold text-gray-800 group-hover:text-white">
                  {analyticsData.total_kyc || 0}
                </h2>
              </>
            ) : (
              <ShimmerCard />
            )}
          </div>

          <div className="group bg-white p-5 rounded-xl shadow hover:shadow-lg transition border-l-4 border-yellow-500 hover:bg-yellow-500">
            {analyticsData && !loading ? (
              <>
                <p className="text-sm font-medium text-gray-500 group-hover:text-white">
                  Total DSR
                </p>
                <h2 className="text-2xl font-bold text-gray-800 group-hover:text-white">
                  {analyticsData.total_dsr || 0}
                </h2>
              </>
            ) : (
              <ShimmerCard />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SalesAnalytics;